import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Smartphone, BellRing, Mail, SlidersHorizontal, Check, ArrowRight, Heart } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { InstallCard, isPhone, isStandalone } from "@/components/InstallGuide";
import PushToggle from "@/components/PushToggle";
import { NotifMatrix } from "@/pages/AccountSettings";
import { SocialLinks } from "@/components/Socials";

const STEPS = [
  { icon: Smartphone, title: "Installe l'app" },
  { icon: BellRing, title: "Active tes alertes" },
  { icon: SlidersHorizontal, title: "Choisis tes notifications" },
  { icon: Heart, title: "Suis-nous" },
];
const EMAIL_KEYS = ["reminders", "results", "mentions", "challenges", "periods", "weekly"];

function StepInstall({ installed }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-300">Une icône Ma Petite Ligue sur ton écran d'accueil : l'app s'ouvre en plein écran et peut t'alerter même fermée.
        <b className="text-white"> Sans installation, pas d'alertes sur le téléphone.</b></p>
      <InstallCard />
      {!isPhone() && <p className="text-xs text-slate-400" data-testid="setup-desktop-note">Tu es sur ordinateur : tu peux activer les alertes de l'ordinateur sans installer. Pense à installer l'app sur ton téléphone aussi.</p>}
      {installed && <p className="flex items-center gap-2 text-sm font-semibold text-[#B7F52A]" data-testid="setup-installed"><Check size={15} /> App installée</p>}
    </div>
  );
}

function StepAlerts({ user, patch, setPush }) {
  const ep = user.email_prefs || {};
  const emailOn = EMAIL_KEYS.some((k) => ep[k]);
  return (
    <div className="space-y-3">
      <div className="mpl-card space-y-2 p-4" data-testid="setup-push">
        <p className="flex items-center gap-2 text-sm font-bold"><BellRing size={15} className="text-[#B7F52A]" /> Sur {isPhone() ? "ce téléphone" : "cet ordinateur"}</p>
        <PushToggle onChange={setPush} />
      </div>
      <div className="mpl-card flex items-center justify-between gap-3 p-4" data-testid="setup-email">
        <span><b className="flex items-center gap-2 text-sm"><Mail size={15} className="text-[#B7F52A]" /> Par courriel</b><span className="text-xs text-slate-400">À {user.email}</span></span>
        <Switch checked={emailOn} onCheckedChange={(v) => patch({ email_prefs: Object.fromEntries(EMAIL_KEYS.map((k) => [k, v])) }, true)} data-testid="setup-email-switch" />
      </div>
      <p className="text-xs text-slate-500">Un, l'autre ou les deux. Tu affines sujet par sujet à l'étape suivante.</p>
    </div>
  );
}

export default function Setup() {
  const { user, setUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [push, setPush] = useState(null);
  const step = Math.min(Math.max(Number(params.get("etape") || 1), 1), STEPS.length) - 1;
  const go = (i) => setParams({ etape: String(i + 1) }, { replace: true });
  const patch = async (body, quiet) => {
    try { const { data } = await api.patch("/me", body); setUser(data); if (!quiet) toast.success("Enregistré"); return true; } catch (e) { toast.error(errMsg(e)); return false; }
  };
  const installed = user.setup?.installed || isStandalone();
  const next = async () => {
    if (step === 1) {
      const emailOn = EMAIL_KEYS.some((k) => user.email_prefs?.[k]);
      if (!emailOn && !push) { toast.error("Active au moins un canal : le téléphone ou le courriel."); return; }
      if (!(await patch({ setup: { alerts: true } }, true))) return;
    }
    if (step === 2 && !(await patch({ setup: { prefs: true } }, true))) return;
    if (step === 3) { toast.success("C'est réglé ! Tu peux tout modifier dans Profil → Paramètres."); nav("/tableau-de-bord"); return; }
    go(step + 1);
  };
  const S = STEPS[step];
  return (
    <div className="mx-auto max-w-2xl space-y-5" data-testid="setup-page">
      <ol className="flex gap-2" data-testid="setup-progress">{STEPS.map((s, i) => (
        <li key={s.title} className="flex-1"><button onClick={() => go(i)} className="w-full text-left" data-testid={`setup-step-${i + 1}`}>
          <span className={`block h-1.5 rounded-full transition-colors ${i <= step ? "bg-[#B7F52A]" : "bg-white/10"}`} />
          <span className={`mt-1.5 block text-[11px] font-bold uppercase tracking-wide ${i === step ? "text-white" : "text-slate-500"}`}>{i + 1}. {s.title}</span></button></li>))}</ol>
      <h1 className="flex items-center gap-2 font-display text-4xl font-extrabold uppercase"><S.icon size={26} className="text-[#B7F52A]" /> {S.title}</h1>
      {step === 0 && <StepInstall installed={installed} />}
      {step === 1 && <StepAlerts user={user} patch={patch} setPush={setPush} />}
      {step === 2 && <NotifMatrix user={user} patch={patch} />}
      {step === 3 && (
        <div className="space-y-3" data-testid="setup-follow">
          <p className="text-sm text-slate-300">Les actus de Ma Petite Ligue, les classements de la semaine et les meilleurs défis : abonne-toi à <b className="text-white">@mapetiteligue</b>.</p>
          <p className="rounded-xl bg-[#B7F52A]/10 p-3 text-sm text-[#B7F52A]" data-testid="setup-follow-bonus"><b>+15 pts par réseau</b>, soit 30 pts, ajoutés au classement de la saison de chacune de tes ligues, actuelles et futures (sauf si l'organisateur a coupé ce bonus).</p>
          <SocialLinks testPrefix="setup-follow" bonus done={user.setup || {}} onOpen={(k) => patch({ setup: { [k]: true } }, true)} />
        </div>)}
      <div className="flex items-center justify-between gap-3">
        <Link to="/tableau-de-bord" className="text-xs font-semibold text-slate-400 hover:text-white" data-testid="setup-later">Plus tard</Link>
        <button onClick={next} className="mpl-btn-primary" data-testid="setup-next">{step === 3 ? "Terminer" : step === 0 && !installed ? "Continuer sans installer" : "Continuer"} <ArrowRight size={15} /></button>
      </div>
    </div>
  );
}
